<script setup lang="ts">
/**
 * App shell — brief §10, restructured by brief 11-layout2 §1: on-demand
 * drawers at every screen size (no permanently pinned side panels, including
 * desktop); tool strip + Tessellation/Layers tab access dock to opposite
 * edges in landscape (swap together under ui.dominantSide) or stack as a
 * top row + bottom tab bar in portrait — orientation-driven, not device-
 * category-driven, via useLayoutMode.
 * Autosave: debounced 2s after the last change + on pagehide (brief §18).
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { IconHexagon, IconStack2 } from '@tabler/icons-vue'
import TileCanvas from '@/components/canvas/TileCanvas.vue'
import Toolbar from '@/components/tools/Toolbar.vue'
import ToolStrip from '@/components/tools/ToolStrip.vue'
import LayerPanel from '@/components/tools/LayerPanel.vue'
import PatternPanel from '@/components/patterns/PatternPanel.vue'
import PaywallSheet from '@/components/paywall/PaywallSheet.vue'
import GetAppCta from '@/components/paywall/GetAppCta.vue'
import GalleryView from '@/components/sketchbook/GalleryView.vue'
import StorageBanner from '@/components/sketchbook/StorageBanner.vue'
import OnboardingOverlay from '@/components/help/OnboardingOverlay.vue'
import HelpSheet from '@/components/help/HelpSheet.vue'
import SettingsSheet from '@/components/help/SettingsSheet.vue'
import ExportSheet from '@/components/export/ExportSheet.vue'
import RenderPsdSheet from '@/components/export/RenderPsdSheet.vue'
import { useLayoutMode } from '@/composables/useLayoutMode'
import { useSwipeDismiss } from '@/composables/useSwipeDismiss'
import { useEntitlementStore } from '@/stores/entitlement'
import { useLayersStore } from '@/stores/layers'
import { usePatternStore } from '@/stores/pattern'
import { useSketchbookStore } from '@/stores/sketchbook'
import { useToolsStore } from '@/stores/tools'
import { useUiStore } from '@/stores/ui'
import { initOpenWithPsd } from '@/services/openWithPsd'
import { ensureMigrationComplete } from './services/sketchRepository'

const entitlement = useEntitlementStore()
const layers = useLayersStore()
const patternStore = usePatternStore()
const tools = useToolsStore()
const sketchbook = useSketchbookStore()
const ui = useUiStore()
const { isLandscape, isInlinePanel } = useLayoutMode()

/** The Tessellation/Layers panel, opened on demand as a drawer — never
 *  permanently shown, at any screen size (brief 11-layout2 §1). */
const activePanel = ref<'pattern' | 'layers' | null>(null)

function togglePanel(panel: 'pattern' | 'layers'): void {
	activePanel.value = activePanel.value === panel ? null : panel
}

/** Swipe-down (bottom sheet) / swipe-toward-edge (side drawer) to dismiss —
 *  bound to each drawer's own small handle element (useSwipeDismiss.ts), not
 *  the whole sheet, so it never competes with internal scroll containers or
 *  Configure pattern's sliders. Two independent instances: drag state must
 *  not leak between the two drawers. Direction matches whichever way each
 *  layout's own closed-state transform already goes (see .drawer CSS below). */
const patternDrawerEl = ref<HTMLElement | null>(null)
const layersDrawerEl = ref<HTMLElement | null>(null)
const dismissAxis = (): 'x' | 'y' => (isLandscape.value ? 'x' : 'y')
const dismissSign = (): 1 | -1 => (isLandscape.value ? (ui.dominantSide === 'right' ? -1 : 1) : 1)
const patternDismiss = useSwipeDismiss(patternDrawerEl, {
	axis: dismissAxis,
	sign: dismissSign,
	onDismiss: () => { if (activePanel.value === 'pattern') activePanel.value = null },
})
const layersDismiss = useSwipeDismiss(layersDrawerEl, {
	axis: dismissAxis,
	sign: dismissSign,
	onDismiss: () => { if (activePanel.value === 'layers') activePanel.value = null },
})

/** The tool strip docks opposite the tab access in landscape (dominant
 *  side); in portrait it's always a row under the top bar. */
const toolStripSide = computed(() =>
	isLandscape.value ? ui.dominantSide : 'bottom',
)

let saveTimer: ReturnType<typeof setTimeout> | null = null

async function autosave(): Promise<void> {
	if (!sketchbook.currentSketchId) {
		await sketchbook.newSketch(
			patternStore.currentPatternId,
			patternStore.currentVariantId,
			{
				keepContent: true,
			},
		)
	} else {
		await sketchbook.saveCurrent()
	}
}

watch(
	() => layers.revision,
	() => {
		if (saveTimer) clearTimeout(saveTimer)
		saveTimer = setTimeout(() => void autosave(), 2000)
	},
)

function onPageHide(): void {
	if (layers.isDirty) void autosave()
}

/** Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z (or Ctrl+Y) = redo, dispatched to the active layer. */
function onKeyDown(e: KeyboardEvent): void {
	if (!(e.ctrlKey || e.metaKey)) return
	const target = e.target as HTMLElement | null
	if (
		target &&
		(target.tagName === 'INPUT' ||
			target.tagName === 'TEXTAREA' ||
			target.isContentEditable)
	) {
		return // let text fields handle their own undo
	}
	const key = e.key.toLowerCase()
	if (key === 'z' && !e.shiftKey) {
		e.preventDefault()
		layers.undo()
	} else if ((key === 'z' && e.shiftKey) || key === 'y') {
		e.preventDefault()
		layers.redo()
	}
}

/** Reopen the sketch from last visit (and its pattern), if it still exists. */
async function restoreLast(): Promise<void> {
	await sketchbook.refresh()
	const lastId = sketchbook.lastSketchId()
	const meta = lastId
		? sketchbook.sketches.find((m) => m.id === lastId)
		: undefined
	if (meta) {
		await sketchbook.openSketch(meta.id)
		patternStore.applyWpg(meta.variantId, meta.artistTile, meta.latticeParams)
		tools.setColourCount(meta.colourCount ?? 1)
		// Direct assignment, not selectColour() — restoring a sketch shouldn't
		// itself flip sketchColourActive for one that was never coloured.
		tools.baseColourIndex = meta.baseColourIndex ?? 0
	}
}

onMounted(async () => {
	void entitlement.init()
	await ensureMigrationComplete().catch((err) => {
		console.error('Error migrating sketches:', err)
	})
	void restoreLast()
	initOpenWithPsd()
	window.addEventListener('pagehide', onPageHide)
	document.addEventListener('visibilitychange', onPageHide)
	window.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
	window.removeEventListener('pagehide', onPageHide)
	document.removeEventListener('visibilitychange', onPageHide)
	window.removeEventListener('keydown', onKeyDown)
	if (saveTimer) clearTimeout(saveTimer)
})
</script>

<template>
	<div class="app-shell">
		<StorageBanner />
		<Toolbar />
		<div
			class="app-body"
			:class="[
				isLandscape ? 'landscape' : 'portrait',
				`dom-${ui.dominantSide}`,
			]"
		>
			<nav class="tab-access" :class="{ vertical: isLandscape }">
				<button
					class="nav-tab"
					:class="{ active: activePanel === 'pattern' }"
					:aria-pressed="activePanel === 'pattern'"
					@click="togglePanel('pattern')"
				>
					<IconHexagon :size="24" stroke-width="1.75" />
					<span>Tessellation</span>
				</button>
				<button
					class="nav-tab"
					:class="{ active: activePanel === 'layers' }"
					:aria-pressed="activePanel === 'layers'"
					@click="togglePanel('layers')"
				>
					<IconStack2 :size="24" stroke-width="1.75" />
					<span>Layers</span>
				</button>
			</nav>

			<main class="canvas-area">
				<TileCanvas />
			</main>

			<div class="tool-strip-slot">
				<ToolStrip :side="toolStripSide" />
			</div>

			<aside
				ref="patternDrawerEl"
				class="drawer pattern-drawer"
				:class="{
					open: activePanel === 'pattern',
					inline: isInlinePanel,
					landscape: isLandscape,
				}"
				:style="patternDismiss.dragStyle.value"
			>
				<div
					class="drawer-handle"
					@pointerdown="patternDismiss.onPointerDown"
					@pointermove="patternDismiss.onPointerMove"
					@pointerup="patternDismiss.onPointerUp"
					@pointercancel="patternDismiss.onPointerUp"
				/>
				<div class="panel-content">
					<PatternPanel />
					<GetAppCta />
				</div>
			</aside>
			<aside
				ref="layersDrawerEl"
				class="drawer layers-drawer"
				:class="{
					open: activePanel === 'layers',
					inline: isInlinePanel,
					landscape: isLandscape,
				}"
				:style="layersDismiss.dragStyle.value"
			>
				<div
					class="drawer-handle"
					@pointerdown="layersDismiss.onPointerDown"
					@pointermove="layersDismiss.onPointerMove"
					@pointerup="layersDismiss.onPointerUp"
					@pointercancel="layersDismiss.onPointerUp"
				/>
				<div class="panel-content">
					<LayerPanel />
				</div>
			</aside>

			<div
				v-if="activePanel"
				class="panel-backdrop"
				@click="activePanel = null"
			/>
		</div>
		<PaywallSheet />
		<GalleryView />
		<HelpSheet />
		<SettingsSheet />
		<ExportSheet />
		<RenderPsdSheet />
		<OnboardingOverlay />
	</div>
</template>

<style scoped>
.app-shell {
	height: 100%;
	display: flex;
	flex-direction: column;
	/* Keep all chrome clear of the system bars on edge-to-edge displays. The
     inset strips are painted in the chrome surface colour so the status bar
     and gesture pill sit over what looks like the app frame, not a seam. */
	padding-top: var(--safe-top);
	padding-bottom: var(--safe-bottom);
	padding-left: var(--safe-left);
	padding-right: var(--safe-right);
	background: var(--ui-surface);
	overflow: hidden;
}

.app-body {
	flex: 1;
	position: relative;
	display: grid;
	min-height: 0;
	overflow: hidden;
}

.canvas-area {
	grid-area: canvas;
	min-width: 0;
	min-height: 0;
	z-index: 0;
}

.tool-strip-slot {
	grid-area: toolstrip;
	min-width: 0;
	min-height: 0;
	overflow-y: auto;
	z-index: 50;
}

.tab-access {
	grid-area: tabaccess;
	display: flex;
	background: var(--ui-surface);
	z-index: 30;
}

.nav-tab {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	min-height: 48px;
	padding: 6px 8px;
	font-size: 11px;
	color: var(--text-muted);
	background: transparent;
	border: none;
	cursor: pointer;
}

.tab-access.vertical {
	flex-direction: column;
}

.tab-access.vertical .nav-tab {
	flex-direction: column;
	gap: 2px;
}

.nav-tab.active {
	color: var(--accent);
}

/* Landscape (any width — brief 11-layout2 §1: narrow/docked is the default
   everywhere except tablet-portrait): tool strip docks to one edge, tab
   access to the other, swapping together under ui.dominantSide. Panels open
   as slim overlay drawers from the tab-access edge, never permanently
   pinned. */
.app-body.landscape {
	grid-template-rows: 1fr;
}

.app-body.landscape.dom-right {
	grid-template-columns: 5rem 1fr 5rem;
	grid-template-areas: 'tabaccess canvas toolstrip';
}

.app-body.landscape.dom-left {
	grid-template-columns: 5rem 1fr 5rem;
	grid-template-areas: 'toolstrip canvas tabaccess';
}

.app-body.landscape .tab-access {
	border-right: 1px solid var(--ui-border);
}

.app-body.landscape.dom-left .tab-access {
	border-right: none;
	border-left: 1px solid var(--ui-border);
}

.app-body.landscape .drawer {
	position: absolute;
	top: 0;
	bottom: 0;
	width: min(320px, 88vw);
	border: none;
	box-shadow: 0 0 24px rgba(0, 0, 0, 0.35);
	transition: transform 0.22s ease;
}

.app-body.landscape.dom-right .drawer {
	left: 0;
	border-right: 1px solid var(--ui-border);
	transform: translateX(-105%);
}

.app-body.landscape.dom-left .drawer {
	right: 0;
	border-left: 1px solid var(--ui-border);
	transform: translateX(105%);
}

.app-body.landscape .drawer.open {
	transform: translateX(0);
}

/* Portrait (default — brief 11-layout2 §1): tool strip is a row under the
   top bar; Tessellation/Layers access stays a bottom tab bar, unchanged
   from the existing mobile spec. Panels open as bottom-sheet drawers at
   every width — "full-width inline" (tablet portrait, .inline) only changes
   the panel's OWN internal layout (§2), never makes it permanently shown. */
.app-body.portrait {
	grid-template-columns: 1fr;
	grid-template-rows: auto minmax(0, 1fr) auto;
	grid-template-areas:
		'toolstrip'
		'canvas'
		'tabaccess';
}

.app-body.portrait .tool-strip-slot {
	border-bottom: 1px solid var(--ui-border);
}

.app-body.portrait .tab-access {
	border-top: 1px solid var(--ui-border);
}

.app-body.portrait .drawer {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	max-height: 70vh;
	border: none;
	border-radius: 14px 14px 0 0;
	box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.35);
	padding-bottom: var(--safe-bottom);
	transform: translateY(105%);
	transition: transform 0.22s ease;
}

.app-body.portrait .drawer.open {
	transform: translateY(0);
}

.drawer {
	background: var(--ui-surface);
	z-index: 90;
	overflow-y: auto;
}

.drawer .panel-content {
	max-height: 100%;
	overflow-y: auto;
}

/* Swipe-down/toward-edge-to-dismiss grab handle (brief addendum §4) — a
   dedicated hit area, not the whole sheet, so it never fights the sheet's
   own scroll containers or Configure pattern's sliders (useSwipeDismiss.ts).
   touch-action: none stops the browser's own scroll/pan gesture from
   competing with the pointer-capture drag. */
.drawer-handle {
	touch-action: none;
	cursor: grab;
}

.app-body.portrait .drawer-handle {
	width: 100%;
	height: 22px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.app-body.portrait .drawer-handle::before {
	content: '';
	width: 36px;
	height: 4px;
	border-radius: 2px;
	background: var(--ui-border);
}

.app-body.landscape .drawer-handle {
	position: absolute;
	top: 0;
	bottom: 0;
	width: 22px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.app-body.landscape.dom-right .drawer-handle {
	right: 0;
}

.app-body.landscape.dom-left .drawer-handle {
	left: 0;
}

.app-body.landscape .drawer-handle::before {
	content: '';
	width: 4px;
	height: 36px;
	border-radius: 2px;
	background: var(--ui-border);
}

.panel-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(8, 8, 16, 0.45);
	z-index: 80;
}

.canvas-area,
.tool-strip-slot,
.tab-access {
	position: relative;
	overflow: visible;
}
</style>
